import {
  Media,
  MediaType,
  useCreateMutation,
  useCurrentWorkspace,
  useDeleteMutation,
  useFindByQuery,
  useUpdateMutation,
  useWorkspaceCategories,
} from '@gripp/shared-logic';
import {
  Button,
  CategorySelector,
  Colors,
  ConfirmationOverlay,
  ControlledTextInput,
  CustomAlert,
  Spacing,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAudioPlayer } from 'expo-audio';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Control,
  useForm,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import Toast from 'react-native-toast-message';
import * as yup from 'yup';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const audioSource = require('../../../static-assets/delete_pop.mp3');

const CREATE_CATEGORY = `
  mutation createCategory($input: CreateCategoryInput!) {
    createCategory(input: $input) {
      id
      name
    }
  }
`;

type Category = {
  id: string;
  name: string;
};

type EditFileFormProps = {
  onComplete: () => void;
  fileId: string;
  /** Pre-loaded media — skips the internal fetch. Required by the Base (Crop Circle) variant. */
  media?: Media;
  /** Called after the file is saved, before `onComplete`. Use to run additional mutations (e.g. update product). */
  onAfterSave?: () => Promise<void>;
  /** Overrides the default hard-delete. Use to remove the file from a parent entity instead. */
  onDelete?: () => Promise<void>;
  /** Called after a successful default hard-delete in place of `onComplete`. Use to navigate somewhere other than the preview (which no longer exists). */
  onDeleteSuccess?: () => void;
  /** Called when the user taps the category add button; opens a third panel. */
  onCategoryEdit?: () => void;
  /** Controlled categories driven by a parent (e.g. third-panel picker). */
  externalCategories?: Category[];
  /** Notifies parent when categories change so it can keep its own state in sync. */
  onCategoriesChange?: (cats: Category[]) => void;
};

export const EditFileForm = (props: EditFileFormProps) => {
  return <EditFileFormBase {...props} />;
};

type FormValues = {
  name: string;
  categories: Category[];
  newCategoryName?: string;
  linkUrl?: string;
};

type EditFileFormLogic = {
  control: Control<FormValues>;
  handleSubmit: UseFormHandleSubmit<FormValues>;
  watch: UseFormWatch<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  isFileLoading: boolean;
  isDirty: boolean;
  canSave: boolean;
  fileName: string;
  isLink: boolean;
  selectedCategories: Category[];
  availableCategories: Category[];
  isCreatingCategory: boolean;
  isModalOpen: boolean;
  showDeleteAlert: boolean;
  setIsModalOpen: (open: boolean) => void;
  setShowDeleteAlert: (show: boolean) => void;
  closeCategoryModal: () => void;
  handleCreateCategory: (name: string) => Promise<Category>;
  onSubmit: (data: FormValues) => Promise<void>;
  onFileDelete: () => Promise<void>;
};

const useEditFileFormLogic = ({
  onComplete,
  fileId,
  media: mediaProp,
  onAfterSave,
  onDelete,
  onDeleteSuccess,
  externalCategories,
}: EditFileFormProps): EditFileFormLogic => {
  const { t } = useTranslation();
  const currentWorkspace = useCurrentWorkspace();

  const filter = useMemo(() => ({ id: { eq: fileId } }), [fileId]);

  const { data, isLoading: isQueryLoading } = useFindByQuery({
    modelName: 'media',
    query: FIND_FILE,
    variables: { filter },
    options: { enabled: !mediaProp },
  });
  const isFileLoading = mediaProp ? false : isQueryLoading;

  const { categories } = useWorkspaceCategories();

  const { mutateAsync: createCategory, isPending: isCreatingCategory } =
    useCreateMutation({
      modelName: 'category',
      query: CREATE_CATEGORY,
    });

  const [fileName, setFileName] = useState('');
  const [, setFileCategories] = useState<Category[]>([]);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const player = useAudioPlayer(audioSource);

  const { mutateAsync } = useUpdateMutation({
    modelName: 'media',
    query: UPDATE_FILE,
  });

  const { mutateAsync: deleteFileAsync } = useDeleteMutation({
    modelName: 'media',
    query: DELETE_FILE,
  });

  const formSchema = yup.object({
    name: yup.string().required('File name is required'),
    categories: yup
      .array()
      .of(
        yup.object({
          id: yup.string().required(),
          name: yup.string().required(),
        })
      )
      .optional(),
    newCategoryName: yup.string().optional(),
    linkUrl: yup
      .string()
      .url(t('file.create.validation.link_invalid'))
      .optional(),
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(formSchema) as never,
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      categories: [],
      newCategoryName: '',
      linkUrl: '',
    },
  });

  const selectedCategories = (watch('categories') || []) as Category[];
  const mediaType = mediaProp?.type ?? data?.items?.[0]?.type;
  const isLink = mediaType === MediaType.Link;
  const canSave = isDirty && !isSubmitting;

  // Initialise form from internal fetch (legacy path with no mediaProp)
  useEffect(() => {
    if (!data || !data.items.length) return;

    const file = data.items[0];
    reset({
      name: file.name,
      categories: file.categories || [],
      newCategoryName: '',
      linkUrl: file.url ?? '',
    });
    setFileName(file.name);
    setFileCategories(file.categories || []);
  }, [data, reset]);

  // Initialise form from pre-loaded media (Crop Circle path)
  useEffect(() => {
    if (!mediaProp) return;
    reset({
      name: mediaProp.name ?? '',
      categories: (mediaProp.categories as Category[]) ?? [],
      newCategoryName: '',
      linkUrl: mediaProp.url ?? '',
    });
    setFileName(mediaProp.name ?? '');
    setFileCategories((mediaProp.categories as Category[]) ?? []);
  }, [mediaProp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync external controlled categories → form (driven by third-panel picker)
  useEffect(() => {
    if (!externalCategories) return;
    const current = (watch('categories') ?? []) as Category[];
    const formIds = current
      .map((c) => c.id)
      .sort()
      .join(',');
    const extIds = externalCategories
      .map((c) => c.id)
      .sort()
      .join(',');
    if (formIds !== extIds) {
      setValue('categories', externalCategories, { shouldDirty: true });
    }
  }, [externalCategories]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateCategory = useCallback(
    async (name: string) => {
      try {
        const result = await createCategory({
          input: {
            name: name.trim(),
            workspace: { id: currentWorkspace.id },
          },
        });

        if (result?.id) {
          return { id: result.id, name: result.name };
        }
        throw new Error('Failed to create category');
      } catch (error) {
        console.error('Failed to create category:', error);
        throw error;
      }
    },
    [createCategory, currentWorkspace?.id]
  );

  const closeCategoryModal = () => {
    setIsModalOpen(false);
    setValue('newCategoryName', '', { shouldDirty: false });
  };

  const showToast = () => {
    Toast.show({
      type: 'blackToast',
      text1: 'Woosh!',
      text2: `${fileName} has been deleted.`,
      position: 'bottom',
      autoHide: true,
      visibilityTime: 2500,
    });
  };

  const onSubmit = async (formData: FormValues) => {
    const input: {
      id: string;
      name: string;
      categories: { id: string }[];
      url?: string;
    } = {
      id: fileId,
      name: formData.name,
      categories: formData.categories?.map((c) => ({ id: c.id })) || [],
    };
    if (formData.linkUrl !== undefined) {
      input.url = formData.linkUrl;
    }

    await mutateAsync({ input });
    await onAfterSave?.();
    onComplete();
  };

  const onFileDelete = async () => {
    if (onDelete) {
      await onDelete();
      onComplete();
      return;
    }
    await deleteFileAsync({ input: { id: fileId } });
    showToast();
    player.play();
    if (onDeleteSuccess) {
      onDeleteSuccess();
    } else {
      onComplete();
    }
  };

  return {
    control,
    handleSubmit,
    watch,
    setValue,
    isFileLoading,
    isDirty,
    canSave,
    fileName,
    isLink,
    selectedCategories,
    availableCategories: categories ?? [],
    isCreatingCategory,
    isModalOpen,
    showDeleteAlert,
    setIsModalOpen,
    setShowDeleteAlert,
    closeCategoryModal,
    handleCreateCategory,
    onSubmit,
    onFileDelete,
  };
};

// TODO: Crop Circle Redesign | Rename to EditFileForm after feature is fully rolled out
const EditFileFormBase = (props: EditFileFormProps) => {
  const { t } = useTranslation();
  const slideLayout = useSlideLayoutSafe();
  const logic = useEditFileFormLogic(props);
  const {
    control,
    handleSubmit,
    setValue,
    isFileLoading,
    canSave,
    fileName,
    isLink,
    selectedCategories,
    availableCategories,
    showDeleteAlert,
    setShowDeleteAlert,
    handleCreateCategory,
    onSubmit,
    onFileDelete,
  } = logic;

  const handleCropCircleDeletePress = () => {
    // Falls back to a local CustomAlert when no SlideLayout context is
    // available (e.g. when this form is rendered inside a modal), so the
    // delete action still works outside the panelized flow.
    if (!slideLayout) {
      setShowDeleteAlert(true);
      return;
    }
    slideLayout.setColumnOverlay(
      <ConfirmationOverlay
        title={t('common.confirmDelete', { name: fileName })}
        subtitle={t('common.deleteWarning')}
        confirmLabel={t('common.delete')}
        onConfirm={async () => {
          // onFileDelete already navigates via onDeleteSuccess/onComplete
          await onFileDelete();
          slideLayout.setColumnOverlay(null);
        }}
        cancelLabel={t('topNav.customAlert.cancel')}
        onCancel={() => slideLayout.setColumnOverlay(null)}
      />
    );
  };

  if (isFileLoading) return null;

  return (
    <>
      <ScrollView
        contentContainerStyle={cropCircleStyles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <ControlledTextInput
          control={control}
          name="name"
          placeholder={t('file.create.title')}
          hideBorder
        />
        {isLink && (
          <ControlledTextInput
            control={control}
            name="linkUrl"
            placeholder={t('file.create.linkUrl')}
            hideBorder
          />
        )}
        <CategorySelector
          selectedCategories={selectedCategories}
          availableCategories={availableCategories}
          onCategoriesChange={(cats) => {
            setValue('categories', cats, { shouldDirty: true });
            props.onCategoriesChange?.(cats);
          }}
          onCreateCategory={handleCreateCategory}
          onSelectorEdit={props.onCategoryEdit}
        />
      </ScrollView>
      <View style={cropCircleStyles.footer}>
        <Button
          style={cropCircleStyles.saveButton}
          onPress={canSave ? handleSubmit(onSubmit) : undefined}
          state={canSave ? 'active' : 'default'}
          disabled={!canSave}
        >
          {t('asset.save')}
        </Button>
        <Button
          variant="rectangular"
          state="outline"
          style={cropCircleStyles.deleteButton}
          onPress={handleCropCircleDeletePress}
          size="large"
        >
          {t('asset.delete.button')}
        </Button>
      </View>
      <CustomAlert
        visible={showDeleteAlert}
        hideAlert={() => setShowDeleteAlert(false)}
        title={
          <>
            {t('file.deleteFile.alertBody1')} <b>{fileName}</b>?{' '}
          </>
        }
        primaryButtonLabel={t('file.deleteFile.delete')}
        secondaryButtonLabel={t('file.deleteFile.cancel')}
        action={() => onFileDelete()}
        containerStyle={styles.deleteAlertContainer}
      />
    </>
  );
};

const styles = StyleSheet.create({
  deleteAlertContainer: {
    width: 350,
  },
});

const cropCircleStyles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    paddingVertical: Spacing.basePadding.paddingVertical,
    paddingHorizontal: 16,
    paddingBottom: 164,
    gap: 14,
  },
  footer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: Spacing.baseMargin.base,
    paddingVertical: 20,
    backgroundColor: Colors.brand.white,
    flexDirection: 'column',
    alignItems: 'center',
  },
  saveButton: {
    marginBottom: Spacing.baseMargin.base,
    borderRadius: 2,
    width: '100%',
    height: 48,
  },
  deleteButton: {
    marginBottom: 12,
    borderWidth: 0,
    width: '100%',
  },
});

const UPDATE_FILE = `
  mutation UpdateFile($input: MutateMediaInput!) {
    updateMedia(input: $input) {
      id
    }
  }
`;

const DELETE_FILE = `
  mutation DeleteFile($input: MutateMediaInput!) {
    deleteMedia(input: $input) {
      id
    }
  }
`;

const FIND_FILE = `
  query FindFiles($filter: MediaFilter) {
    findMedia(filter: $filter) {
      items {
        id
        name
        extension
        status
        type
        url
        categories {
          id
          name
        }
      }
    }
  }
`;
